/**
 * Tour storage helpers — central registry of every interactive page-tour
 * `localStorage` key + a one-shot reset utility used by the "Reset all
 * tours" button in My Access and the admin footer link.
 *
 * Keep `TOURS` in sync with each `<TourOverlay>` instance (see
 * `RequestedChangesTour`, `EvidenceRulesTour`, `WarrantyChatTour`,
 * `QueueTourOverlay`).
 */
import api from "@/lib/api";

/**
 * Catalog of every interactive tour available in the app, used by the
 * "View all tours" modal + `/tours` page + first-login welcome prompt.
 *
 * Field reference:
 *  - `key`         localStorage flag set when the tour is dismissed
 *  - `title`       short label shown in the catalog
 *  - `description` 1-2 sentence "what does this tour cover?"
 *  - `route`       react-router path to navigate to when the user clicks Watch
 *  - `routeNote`   optional hint when the target route isn't the tour page itself
 *                  (e.g. warranty chat lives on a claim-specific URL)
 *  - `requires`    rights gate: "is_admin" | "is_test_admin" | "email"
 *  - `tone`        TourOverlay tone for the page banner / replay pill
 */
export const TOURS = [
  {
    key: "requested_changes_tour_seen_v1",
    title: "Requested Changes",
    description: "Triage user-submitted change requests through the deploy pipeline.",
    route: "/admin/requested-changes",
    requires: "is_admin",
    tone: "indigo",
  },
  {
    key: "evidence_rules_tour_seen_v1",
    title: "Warranty Evidence Rules",
    description: "Author and tune the AI evidence-rule library used during warranty claims.",
    route: "/admin/warranty/evidence-rules",
    requires: "is_admin",
    tone: "amber",
  },
  {
    key: "warranty_chat_tour_seen_v1",
    title: "Warranty Claim Chat",
    description: "Real-time SSE chat tied to a warranty claim — drag-drop files, evidence rules, transcript.",
    route: "/admin/warranty",
    routeNote: "Open any claim from the list to see the chat tour.",
    requires: "email",
    tone: "violet",
  },
  {
    key: "test_queue_tour_seen_v1",
    title: "Test Admin Queue",
    description: "Approve or reject improvement suggestions before they're deployed to production.",
    route: "/admin/test-queue",
    requires: "is_test_admin",
    tone: "emerald",
  },
];

export const TOUR_STORAGE_KEYS = TOURS.map((t) => t.key);

/** localStorage flag set once the user has dismissed the welcome prompt. */
export const WELCOME_PROMPT_SEEN_KEY = "tours_welcome_seen_v1";

/** iter150X — Aliases accepted as "the prompt has been dismissed". These
 *  exist purely so automated tests / testing agents have a stable
 *  contract: seed ANY of these to "1" (or hit the page with the URL
 *  param `?dismiss_tours=1`) and the welcome prompt stays hidden.
 *
 *  Without this, agents kept guessing keys ("usair_tours_dismissed",
 *  "tours_dismissed", …) and getting blocked by the overlay during
 *  Playwright tests. The first-class key remains `WELCOME_PROMPT_SEEN_KEY`
 *  for the in-app dismiss button; aliases are READ-ONLY shortcuts.
 */
export const WELCOME_PROMPT_DISMISS_ALIASES = [
  "tours_welcome_seen_v1",   // canonical
  "usair_tours_dismissed",   // legacy alias (used by older test scripts)
  "tours_dismissed",         // shorter alias
];

/** Returns true if any of the WELCOME_PROMPT_DISMISS_ALIASES is set to "1"
 *  in localStorage, OR if the current URL has `?dismiss_tours=1`. Used by
 *  WelcomeToursPrompt to decide whether to render. */
export function isWelcomePromptDismissed() {
  if (typeof window === "undefined") return false;
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("dismiss_tours") === "1") return true;
  } catch { /* ignore */ }
  for (const key of WELCOME_PROMPT_DISMISS_ALIASES) {
    try {
      if (window.localStorage.getItem(key) === "1") return true;
    } catch { /* ignore */ }
  }
  return false;
}

/** sessionStorage flag read by `TourOverlay` to auto-start a tour on mount
 *  instead of rendering the banner. Set by "Watch tour" actions in the
 *  catalog right before navigating to the page. */
export const AUTO_START_TOUR_KEY = "auto_start_tour";

/** Returns true if the given tour's `requires` gate is satisfied by the
 *  shape of the logged-in user object (matches usair_user / /me). */
export function userCanSeeTour(user, tour) {
  if (!user) return false;
  if (tour.requires === "is_test_admin") return Boolean(user.is_test_admin || user.is_super_admin);
  if (tour.requires === "is_admin") return Boolean(user.is_admin || user.is_super_admin);
  if (tour.requires === "email") return Boolean(user.email);
  return true;
}

/** Has the given storageKey been dismissed (i.e. tour seen)? */
export function hasSeenTour(storageKey) {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(storageKey) === "1"; }
  catch { return false; }
}

/**
 * Clears every tour "seen" flag from localStorage so the on-page banners
 * re-appear on the next visit. Returns the number of keys actually cleared
 * (a key only counts if it had a value before). Fires a best-effort
 * backend reset so cross-device progress stays in sync.
 */
export function resetAllTours() {
  if (typeof window === "undefined") return 0;
  let cleared = 0;
  for (const key of TOUR_STORAGE_KEYS) {
    try {
      if (window.localStorage.getItem(key) !== null) cleared += 1;
      window.localStorage.removeItem(key);
    } catch {
      /* localStorage blocked — silent */
    }
  }
  // Best-effort backend reset; ignore errors.
  try { resetServerProgress(); } catch { /* noop */ }
  return cleared;
}

/**
 * Mark a tour as needing to auto-start on the *next* page load by clearing
 * its dismissed flag and dropping the auto-start signal into sessionStorage.
 * `TourOverlay` consumes this flag during mount.
 */
export function armAutoStart(storageKey) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey);
    window.sessionStorage.setItem(AUTO_START_TOUR_KEY, storageKey);
  } catch { /* ignore */ }
}

// ─── Backend-synced per-user progress (iter146d) ─────────────────
// Lightweight cross-device tracking so the welcome modal can show a
// completion progress bar even when the user logs in on a new
// machine. Best-effort on every call — a failed network call never
// breaks the UI; localStorage stays the source of truth for the
// per-page banner / replay-pill states.

/** Fetch the user's tour progress from the backend. Returns `null`
 *  when the user is not signed in or the call fails. */
export async function fetchTourProgress() {
  try {
    const { data } = await api.get("/tours/progress");
    return data; // { email, seen[], total, completed, updated_at }
  } catch { return null; }
}

/** Persist a tour as seen on the backend. Fire-and-forget: callers
 *  never need to await this — the localStorage flag is what gates the
 *  per-page UI. Resolves to the latest progress payload or null. */
export async function recordTourSeen(storageKey) {
  if (!TOUR_STORAGE_KEYS.includes(storageKey)) return null;
  try {
    const { data } = await api.post("/tours/seen", { tour_key: storageKey });
    return data;
  } catch { return null; }
}

/** Wipe the user's progress on the backend. Mirrors the catalog's
 *  "Reset all tours" button. */
export async function resetServerProgress() {
  try {
    const { data } = await api.delete("/tours/progress");
    return data;
  } catch { return null; }
}
